import { useMemo, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  HoverCard,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuPositioner,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
  Skeleton,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react'
import { LuCheck, LuInfo, LuListFilter, LuListStart, LuLoader } from 'react-icons/lu'
import type { BankCapacityRow, CapacityStatus, SortDir } from '../capacity.types'
import {
  CAPACITY_UNIT_BY_LABEL,
  formatQuantity,
  parseNumber,
  serviceLabel,
  statusColor,
  statusLabel,
} from '../capacity.utils'
import { ZeitzielBadge } from './ZeitzielCard'

type BankSortField = 'name' | 'place' | 'region' | 'target' | 'sibs'

const stickyColStyles = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 2,
  bg: 'bg',
  borderRightWidth: '1px',
  borderRightColor: 'border',
}
const stickyHeaderStyles = {
  position: 'sticky' as const,
  left: 0,
  zIndex: 4,
  bg: 'bg',
  borderRightWidth: '1px',
  borderRightColor: 'border',
}
const cellProps = { px: 4, py: 4, verticalAlign: 'middle' as const }

const STATUS_FILTERS: { label: string; value: CapacityStatus | 'all' }[] = [
  { label: 'Alle', value: 'all' },
  { label: 'Im Plan', value: 'green' },
  { label: 'Prüfen', value: 'yellow' },
  { label: 'Auffällig', value: 'red' },
]

const SORT_OPTIONS: { label: string; value: BankSortField }[] = [
  { label: 'Alphabetisch', value: 'name' },
  { label: 'PLZ', value: 'place' },
  { label: 'Vxx', value: 'region' },
  { label: 'Zeitziel', value: 'target' },
  { label: 'Aktive SIBs', value: 'sibs' },
]

function sortBankRows(
  rows: BankCapacityRow[],
  field: BankSortField,
  dir: SortDir
): BankCapacityRow[] {
  return [...rows].sort((a, b) => {
    let cmp = 0
    switch (field) {
      case 'name':
        cmp = a.bank.localeCompare(b.bank, 'de')
        break
      case 'place':
        cmp = a.place.localeCompare(b.place, 'de')
        break
      case 'region':
        cmp = a.region.localeCompare(b.region, 'de')
        break
      case 'target':
        cmp = parseNumber(a.target) - parseNumber(b.target)
        break
      case 'sibs':
        cmp = parseNumber(a.sibs) - parseNumber(b.sibs)
        break
    }
    return dir === 'asc' ? cmp : -cmp
  })
}

function StatusLegendItem({
  status,
  description,
}: {
  status: CapacityStatus
  description: string
}) {
  return (
    <HStack gap={2} align="start">
      <Box
        w="10px"
        h="10px"
        borderRadius="full"
        bg={statusColor(status)}
        mt="4px"
        flexShrink={0}
      />
      <Box>
        <Text fontSize="sm" color="fg">
          {statusLabel(status)}
        </Text>
        <Text fontSize="xs" color="fg.subtle">
          {description}
        </Text>
      </Box>
    </HStack>
  )
}

export function BankCapacityTable({
  rows: sourceRows,
  onOpenDetail,
  isLoading = false,
  isLoadingMore = false,
  totalCount,
  tabs,
}: {
  rows: BankCapacityRow[]
  onOpenDetail: (customerId: number) => void
  isLoading?: boolean
  isLoadingMore?: boolean
  totalCount?: number
  tabs?: React.ReactNode
}) {
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState<BankSortField>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [statusFilter, setStatusFilter] = useState<CapacityStatus | 'all'>('all')

  const rows = useMemo(() => {
    let result = sourceRows
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        r =>
          (r.bank ?? '').toLowerCase().includes(q) ||
          (r.blz ?? '').toLowerCase().includes(q) ||
          (r.place ?? '').toLowerCase().includes(q)
      )
    }
    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter)
    }
    return sortBankRows(result, sortField, sortDir)
  }, [sourceRows, search, sortField, sortDir, statusFilter])

  if (isLoading) {
    return (
      <Stack gap={4} data-testid="bank-capacity-table">
        <HStack w="full" gap={3} align="center" flexWrap="wrap">
          <Input
            flex="1"
            minW="240px"
            placeholder="Bank suchen, Name oder Ort eingeben"
            disabled
          />
          {tabs}
        </HStack>
        <Box borderWidth="1px" borderColor="border" borderRadius="xl" overflow="hidden" bg="bg">
          <Stack gap={2} p={4}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} h="48px" borderRadius="md" />
            ))}
          </Stack>
        </Box>
      </Stack>
    )
  }

  return (
    <Stack gap={4} data-testid="bank-capacity-table">
      {/* Toolbar */}
      <HStack w="full" gap={3} align="center" flexWrap="wrap">
        <Input
          flex="1"
          minW="240px"
          placeholder="Bank suchen, Name oder Ort eingeben"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {tabs}

        {/* Sort */}
        <MenuRoot positioning={{ placement: 'bottom-end', gutter: 8 }}>
          <MenuTrigger asChild>
            <Button variant="subtle" size="sm">
              <HStack gap={2}>
                <LuListStart />
                <Text>Sortierung</Text>
              </HStack>
            </Button>
          </MenuTrigger>
          <MenuPositioner>
            <MenuContent minW="220px" bg="bg" borderColor="border">
              {SORT_OPTIONS.map(opt => (
                <MenuItem
                  key={opt.value}
                  value={`sort:${opt.value}`}
                  onClick={() => setSortField(opt.value)}
                >
                  <HStack justify="space-between" w="full">
                    <Text>{opt.label}</Text>
                    {sortField === opt.value ? <LuCheck size={14} /> : null}
                  </HStack>
                </MenuItem>
              ))}
              <MenuSeparator />
              <MenuItem value="dir:asc" onClick={() => setSortDir('asc')}>
                <HStack justify="space-between" w="full">
                  <Text>Aufsteigend</Text>
                  {sortDir === 'asc' ? <LuCheck size={14} /> : null}
                </HStack>
              </MenuItem>
              <MenuItem value="dir:desc" onClick={() => setSortDir('desc')}>
                <HStack justify="space-between" w="full">
                  <Text>Absteigend</Text>
                  {sortDir === 'desc' ? <LuCheck size={14} /> : null}
                </HStack>
              </MenuItem>
            </MenuContent>
          </MenuPositioner>
        </MenuRoot>

        {/* Filter */}
        <MenuRoot positioning={{ placement: 'bottom-end', gutter: 8 }}>
          <MenuTrigger asChild>
            <Button
              variant={statusFilter !== 'all' ? 'solid' : 'subtle'}
              colorPalette={statusFilter !== 'all' ? 'teal' : 'gray'}
              size="sm"
            >
              <HStack gap={2}>
                <LuListFilter />
                <Text>Filter</Text>
              </HStack>
            </Button>
          </MenuTrigger>
          <MenuPositioner>
            <MenuContent minW="200px" bg="bg" borderColor="border">
              {STATUS_FILTERS.map(f => (
                <MenuItem
                  key={f.value}
                  value={`filter:${f.value}`}
                  onClick={() => setStatusFilter(f.value)}
                >
                  <HStack justify="space-between" w="full">
                    <Text>{f.label}</Text>
                    {statusFilter === f.value ? <LuCheck size={14} /> : null}
                  </HStack>
                </MenuItem>
              ))}
            </MenuContent>
          </MenuPositioner>
        </MenuRoot>
      </HStack>

      {/* Table */}
      <Box borderWidth="1px" borderColor="border" borderRadius="xl" overflow="hidden" bg="bg">
        {isLoadingMore && search.trim() && (
          <HStack
            px={4}
            py={2}
            gap={2}
            bg="blue.subtle"
            borderBottomWidth="1px"
            borderColor="blue.muted"
          >
            <Box color="blue.fg" flexShrink={0}>
              <LuLoader size={13} />
            </Box>
            <Text fontSize="xs" color="blue.fg">
              Noch nicht alle Daten geladen – Suche läuft auf {sourceRows.length}
              {totalCount ? ` von ${totalCount}` : ''} Banken. Weitere Ergebnisse erscheinen
              automatisch.
            </Text>
          </HStack>
        )}
        <Box overflowX="auto">
          <Table.Root native minW="900px" w="full" bg="bg">
            <Table.Header bg="bg">
              <Table.Row>
                <Table.ColumnHeader
                  w="240px"
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderColor="border"
                  {...stickyHeaderStyles}
                >
                  <HStack gap={1.5}>
                    <Text>Bank</Text>
                    <HoverCard.Root openDelay={120} closeDelay={80}>
                      <HoverCard.Trigger asChild>
                        <IconButton aria-label="Bank Info" size="xs" variant="ghost">
                          <LuInfo />
                        </IconButton>
                      </HoverCard.Trigger>
                      <HoverCard.Positioner zIndex={9999}>
                        <HoverCard.Content
                          bg="bg"
                          borderWidth="1px"
                          borderColor="border"
                          p={4}
                          shadow="md"
                          maxW="360px"
                        >
                          <HoverCard.Arrow />
                          <Stack gap={2} whiteSpace="normal">
                            <Text fontSize="sm" fontWeight="semibold">
                              Bank
                            </Text>
                            <StatusLegendItem
                              status="green"
                              description="Bank hat bestätigte Bestellungen"
                            />
                            <StatusLegendItem
                              status="yellow"
                              description="Mindestens eine Bestellung ist noch offen"
                            />
                            <StatusLegendItem
                              status="red"
                              description="Bank hat noch keine SIB-Bestellung"
                            />
                          </Stack>
                        </HoverCard.Content>
                      </HoverCard.Positioner>
                    </HoverCard.Root>
                  </HStack>
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                  whiteSpace="nowrap"
                >
                  BLZ
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                  whiteSpace="nowrap"
                >
                  PLZ / Ort
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                >
                  Vxx
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                >
                  Bestellt
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  px={4}
                  py={5}
                  fontSize="md"
                  fontWeight="700"
                  color="fg.default"
                  borderBottomWidth="1px"
                  borderLeftWidth="1px"
                  borderColor="border"
                  whiteSpace="nowrap"
                >
                  Zeitziel
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={6} textAlign="center" py={12} color="fg.subtle">
                    Keine Einträge gefunden.
                  </Table.Cell>
                </Table.Row>
              ) : (
                rows.map(row => (
                  <Table.Row
                    data-testid={`bank-row-${row.customerId}`}
                    key={row.customerId ?? row.bank}
                    cursor="pointer"
                    _hover={{ bg: 'bg.subtle' }}
                    onClick={() => row.customerId !== undefined && onOpenDetail(row.customerId)}
                    css={{
                      '&:hover > td[data-sticky="true"]': {
                        backgroundColor: 'var(--chakra-colors-bg-subtle) !important',
                      },
                    }}
                  >
                    <Table.Cell
                      {...cellProps}
                      w="240px"
                      borderTopWidth="1px"
                      borderColor="border"
                      {...stickyColStyles}
                      data-sticky="true"
                      bg="bg"
                    >
                      <HStack gap={2} align="start">
                        <Box
                          w="10px"
                          h="10px"
                          borderRadius="full"
                          bg={statusColor(row.status)}
                          mt="5px"
                          flexShrink={0}
                        />
                        <Stack gap={0} minW={0}>
                          <Text
                            fontWeight="medium"
                            color="fg"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                          >
                            {row.bank}
                          </Text>
                          <Text fontSize="xs" color="fg.subtle" whiteSpace="nowrap">
                            {row.sibs !== '—'
                              ? `${row.sibs} aktive SIBs`
                              : 'Keine aktive Bestellung'}
                          </Text>
                        </Stack>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell
                      {...cellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      color="fg.muted"
                      whiteSpace="nowrap"
                    >
                      {row.blz}
                    </Table.Cell>
                    <Table.Cell
                      {...cellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      color="fg.muted"
                      whiteSpace="nowrap"
                    >
                      {row.place}
                    </Table.Cell>
                    <Table.Cell
                      {...cellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      color="fg.muted"
                      whiteSpace="nowrap"
                    >
                      {row.region}
                    </Table.Cell>
                    <Table.Cell
                      {...cellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      whiteSpace="nowrap"
                    >
                      <HStack gap={1} wrap="nowrap">
                        {row.ordered.map((val, i) => {
                          const label = serviceLabel(i)
                          return (
                            <Badge
                              key={i}
                              colorPalette={row.status === 'red' ? 'red' : 'gray'}
                              variant="plain"
                              size="sm"
                              whiteSpace="nowrap"
                            >
                              {formatQuantity(val, CAPACITY_UNIT_BY_LABEL[label])}
                            </Badge>
                          )
                        })}
                      </HStack>
                    </Table.Cell>
                    <Table.Cell
                      {...cellProps}
                      borderTopWidth="1px"
                      borderLeftWidth="1px"
                      borderColor="border"
                      whiteSpace="nowrap"
                    >
                      <ZeitzielBadge value={row.target} />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>
    </Stack>
  )
}
